import { useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import {
  ArrowRight,
  ArrowUpRight,
  AudioLines,
  Check,
  CheckCircle2,
  Circle,
  CircleDot,
  Copy,
  GitBranch,
  Layers,
  Loader2,
  LockKeyhole,
  Pause,
  Play,
  Search,
  Sparkles,
  Undo2,
  Upload,
  X,
} from "lucide-react";
import { api } from "./api";
import { AccountPanel } from "./AccountPanel";
import { EntryFrame } from "./Onboarding";
import { PEOPLE, blockers, nextTasks } from "../shared/schema";
import type {
  Evidence,
  Meeting,
  MeetingSummary,
  Person,
  Task,
  WorkEvent,
  Workspace,
  Workstream,
} from "../shared/schema";

export const dateLabel = (value: string, timezone?: string): string =>
  new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: timezone,
  });
export const timeLabel = (seconds: number): string =>
  seconds
    ? `${Math.floor(seconds / 3600) ? `${Math.floor(seconds / 3600)}:` : ""}${Math.floor(
        (seconds % 3600) / 60,
      )
        .toString()
        .padStart(2, "0")}:${Math.floor(seconds % 60)
        .toString()
        .padStart(2, "0")}`
    : "Source passage";
export const statusLabel = (task: Task, tasks: Task[]): string =>
  task.status === "done"
    ? "Completed"
    : task.status === "parked"
      ? "On the horizon"
      : blockers(task, tasks).length || task.waitingFor
        ? "Waiting on input"
        : { ready: "Ready to start", doing: "In progress", waiting: "Waiting" }[task.status];
export function Avatar({ person, small = false }: { person: Person; small?: boolean }) {
  return (
    <span className={`avatar ${person} ${small ? "mini" : ""}`} title={PEOPLE[person].name}>
      {PEOPLE[person].name[0]}
    </span>
  );
}
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="modal-overlay" />
        <Dialog.Content className={`modal-content ${wide ? "wide" : ""}`}>
          <div className="modal-heading">
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Close className="icon-button" aria-label="Close dialog">
              <X size={18} />
            </Dialog.Close>
          </div>
          <Dialog.Description className="modal-description">{description}</Dialog.Description>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
export function Empty({ title, text }: { title: string; text: string }) {
  return (
    <div className="empty-state">
      <CircleDot size={23} />
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}
export function PersonCard({
  person,
  data,
  onOpen,
}: {
  person: Person;
  data: Workspace;
  onOpen: (id: string) => void;
}) {
  const tasks = nextTasks(data.tasks, person, data.constraints, data.streams);
  const focus = tasks[0];
  const stream = data.streams.find((s) => s.id === focus?.streamId);
  return (
    <section className="person-card">
      <div className="person-heading">
        <Avatar person={person} />
        <div>
          <h3>{PEOPLE[person].name}</h3>
          <p>{PEOPLE[person].role}</p>
        </div>
        <span className="tiny-label">{focus?.status === "doing" ? "IN MOTION" : "UP NEXT"}</span>
      </div>
      {focus ? (
        <>
          <button className="focus-task" onClick={() => onOpen(focus.id)}>
            <span className="task-kicker">
              <span className={`stream-dot tone-${stream?.tone ?? 0}`} />
              {stream?.name.toUpperCase()}
            </span>
            <h3>{focus.title}</h3>
            <p>{focus.why}</p>
            <span className="task-footer">
              <span>
                {focus.status === "doing" ? <Play size={12} /> : <Circle size={13} />}{" "}
                {statusLabel(focus, data.tasks)}
              </span>
              <ArrowUpRight size={17} />
            </span>
          </button>
          {tasks.slice(1, 3).map((task) => (
            <button className="secondary-task" key={task.id} onClick={() => onOpen(task.id)}>
              <Circle size={15} />
              <span>{task.title}</span>
              <ArrowRight size={14} />
            </button>
          ))}
        </>
      ) : (
        <Empty
          title="Room to breathe"
          text="No unblocked action is waiting. Check your handoffs or share an update."
        />
      )}
      <div className="person-note">
        <Check size={13} />
        {tasks.length
          ? `${tasks.length} actionable ${tasks.length === 1 ? "step" : "steps"}. One at a time.`
          : "Your plan will adjust when an input arrives."}
      </div>
    </section>
  );
}
export function StreamRow({
  stream,
  tasks,
  onClick,
}: {
  stream: Workstream;
  tasks: Task[];
  onClick: () => void;
}) {
  const own = tasks.filter((t) => t.streamId === stream.id);
  return (
    <button className="stream-row" onClick={onClick}>
      <span className={`stream-symbol tone-${stream.tone}`}>
        <Layers size={18} />
      </span>
      <div>
        <h3>{stream.name}</h3>
        <p>{stream.milestone}</p>
      </div>
      <span className="stream-state">
        {stream.state === "parked"
          ? "On the horizon"
          : `${own.filter((t) => t.status === "done").length}/${own.length} complete`}
      </span>
      <ArrowUpRight size={16} />
    </button>
  );
}
export function Handoff({
  task,
  tasks,
  onOpen,
}: {
  task: Task;
  tasks: Task[];
  onOpen: (id: string) => void;
}) {
  const inputs = blockers(task, tasks);
  const from = inputs[0]?.owner;
  return (
    <div className="handoff-card">
      <span className="handoff-direction">
        {from ? <Avatar person={from} small /> : <Circle size={16} />}
        <ArrowRight size={13} />
        <Avatar person={task.owner} small />
      </span>
      <button className="handoff-title" onClick={() => onOpen(task.id)}>
        <h3>{task.title}</h3>
        <ArrowUpRight size={14} />
      </button>
      {inputs.map((input) => (
        <button className="handoff-input" key={input.id} onClick={() => onOpen(input.id)}>
          <span>Needs {PEOPLE[input.owner].name}’s input</span>
          {input.title}
          <ArrowRight size={12} />
        </button>
      ))}
      {task.waitingFor && <p>{task.waitingFor}</p>}
      <span className="muted-label">
        <GitBranch size={13} />
        Releases the next step for {PEOPLE[task.owner].name}
      </span>
    </div>
  );
}
export function EventItem({
  event,
  onOpen,
  timezone,
}: {
  event: WorkEvent;
  onOpen: () => void;
  timezone?: string;
}) {
  return (
    <div className="timeline-item">
      <span className="timeline-dot" />
      <div>
        <button onClick={onOpen}>
          <h3>{event.title}</h3>
        </button>
        <p>{event.detail}</p>
        <small>
          {event.kind === "system" ? "Starting context" : dateLabel(event.createdAt, timezone)}
          {event.meetingId ? " · Meeting context" : ""}
        </small>
      </div>
    </div>
  );
}
export function Workstreams({
  data,
  filter,
  onFilter,
  onTask,
}: {
  data: Workspace;
  filter: string | null;
  onFilter: (id: string | null) => void;
  onTask: (id: string) => void;
}) {
  const [showDone, setShowDone] = useState(false);
  return (
    <>
      <div className="filter-row">
        <div className="filter-chips">
          <button className={!filter ? "selected" : ""} onClick={() => onFilter(null)}>
            All workstreams
          </button>
          {data.streams.map((s) => (
            <button
              className={filter === s.id ? "selected" : ""}
              key={s.id}
              onClick={() => onFilter(s.id)}
            >
              <span className={`stream-dot tone-${s.tone}`} />
              {s.name}
            </button>
          ))}
        </div>
        <label className="check-label">
          <input
            type="checkbox"
            checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)}
          />
          Show completed
        </label>
      </div>
      {data.streams
        .filter((s) => !filter || filter === s.id)
        .map((stream) => (
          <section className="workstream-section" key={stream.id}>
            <div className="workstream-heading">
              <span className={`stream-symbol tone-${stream.tone}`}>
                <Layers size={21} />
              </span>
              <div>
                <h2>{stream.name}</h2>
                <p>{stream.outcome}</p>
              </div>
              <span className="stream-state">
                {stream.state === "parked" ? "On the horizon" : "Active outcome"}
              </span>
            </div>
            <div className="milestone">
              <ArrowRight size={14} />
              <span>Next milestone</span>
              {stream.milestone}
            </div>
            <div className="task-table">
              {data.tasks
                .filter((t) => t.streamId === stream.id && (showDone || t.status !== "done"))
                .map((task) => (
                  <button className="task-table-row" key={task.id} onClick={() => onTask(task.id)}>
                    {task.status === "done" ? (
                      <CheckCircle2 size={17} />
                    ) : task.status === "doing" ? (
                      <Play size={16} />
                    ) : task.status === "parked" ? (
                      <Pause size={16} />
                    ) : (
                      <Circle size={16} />
                    )}
                    <span className="task-table-title">
                      {task.title}
                      <small>{task.why}</small>
                    </span>
                    <span className={`task-status status-${task.status}`}>
                      {statusLabel(task, data.tasks)}
                    </span>
                    <Avatar person={task.owner} small />
                    <ArrowUpRight size={15} />
                  </button>
                ))}
            </div>
          </section>
        ))}
    </>
  );
}
export function Meetings({
  meetings,
  onOpen,
  timezone,
}: {
  meetings: MeetingSummary[];
  timezone?: string;
  onOpen: (id: string) => void;
}) {
  const [filter, setFilter] = useState("");
  return (
    <>
      <div className="meeting-toolbar">
        <div className="search-box">
          <Search size={17} />
          <input
            aria-label="Filter meetings"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Find a conversation…"
          />
        </div>
        <span>{meetings.length} conversations</span>
      </div>
      <div className="meeting-grid">
        {meetings
          .filter((m) => `${m.title} ${m.summary}`.toLowerCase().includes(filter.toLowerCase()))
          .map((meeting) => (
            <button className="meeting-card" key={meeting.id} onClick={() => onOpen(meeting.id)}>
              <div className="meeting-card-top">
                <span className="meeting-icon">
                  <AudioLines size={19} />
                </span>
                <span className={`meeting-state ${meeting.state}`}>
                  {
                    {
                      archived: "Source context",
                      queued: "Queued",
                      processing: "Reading…",
                      complete: "Reconciled",
                      error: "Needs attention",
                    }[meeting.state]
                  }
                </span>
              </div>
              <h3>{meeting.title}</h3>
              <p>
                {meeting.summary ||
                  "Original transcript available. Open to read the conversation and its context."}
              </p>
              <div className="meeting-card-footer">
                <span>
                  {dateLabel(meeting.occurredAt, timezone)} · {meeting.appName}
                </span>
                <span>
                  {meeting.segmentCount} passages <ArrowUpRight size={13} />
                </span>
              </div>
            </button>
          ))}
      </div>
    </>
  );
}

export function TaskDetail({
  task,
  data,
  pending,
  onPatch,
  onEvidence,
  onTask,
  onRelease,
  onEdit,
  onPark,
}: {
  task: Task;
  data: Workspace;
  pending: boolean;
  onPatch: (task: Task, patch: Record<string, unknown>) => Promise<void>;
  onEvidence: (e: Evidence) => void;
  onTask: (id: string) => void;
  onRelease: () => void;
  onEdit: () => void;
  onPark: () => void;
}) {
  const stream = data.streams.find((s) => s.id === task.streamId);
  const inputs = blockers(task, data.tasks);
  return (
    <>
      <div className="detail-title">
        <span className="task-kicker">
          <span className={`stream-dot tone-${stream?.tone ?? 0}`} />
          {stream?.name.toUpperCase()}
        </span>
        <h2>{task.title}</h2>
      </div>
      <div className="detail-meta">
        <label>
          <span>Owner</span>
          <select
            aria-label="Task owner"
            value={task.owner}
            disabled={pending}
            onChange={(e) => {
              void onPatch(task, { owner: e.target.value });
            }}
          >
            <option value="charlton">Charlton</option>
            <option value="david">David</option>
          </select>
        </label>
        <div>
          <span>Status</span>
          <strong>{statusLabel(task, data.tasks)}</strong>
        </div>
      </div>
      <div className="detail-actions">
        <button className="button secondary" onClick={onEdit}>
          Edit details
        </button>
        {task.status !== "done" ? (
          <button
            className="button primary"
            disabled={pending}
            onClick={() => {
              void onPatch(task, { status: "done" });
            }}
          >
            <Check size={15} />
            Mark complete
          </button>
        ) : (
          <button
            className="button secondary"
            disabled={pending}
            onClick={() => {
              void onPatch(task, { status: "ready" });
            }}
          >
            <Undo2 size={15} />
            Reopen task
          </button>
        )}
        {task.status !== "done" && (
          <>
            <button
              className="button secondary"
              disabled={pending || inputs.length > 0 || !!task.waitingFor}
              onClick={() => {
                void onPatch(task, { status: task.status === "doing" ? "ready" : "doing" });
              }}
            >
              <Play size={13} />
              {task.status === "doing" ? "Return to next up" : "Start work"}
            </button>
            <button
              className="button secondary"
              disabled={pending}
              onClick={() => {
                if (task.status === "parked")
                  void onPatch(task, { status: "ready", resumeAt: null });
                else onPark();
              }}
            >
              <Pause size={14} />
              {task.status === "parked" ? "Bring back" : "Park"}
            </button>
          </>
        )}
      </div>
      <section className="detail-section">
        <h3>
          <Sparkles size={15} />
          Why this matters now
        </h3>
        <p>{task.why}</p>
        {task.status !== "done" && (
          <button
            className="text-button"
            disabled={pending}
            onClick={() => {
              void onPatch(task, { prioritize: !task.pinnedAt });
            }}
          >
            {task.pinnedAt
              ? "Remove my priority override"
              : task.owner === data.viewer
                ? "Make this my next priority"
                : `Make this ${PEOPLE[task.owner].name}’s next priority`}{" "}
            <ArrowUpRight size={13} />
          </button>
        )}
      </section>
      {task.dueAt && (
        <section className="detail-section">
          <h3>Deadline</h3>
          <p>{new Date(task.dueAt).toLocaleString(undefined, { timeZone: data.timezone })}</p>
        </section>
      )}
      <section className="detail-section">
        <h3>The brief</h3>
        <p>{task.brief || "No additional brief yet. Use Edit details to add context."}</p>
      </section>
      <section className="detail-section">
        <h3>
          <CheckCircle2 size={15} />
          What done looks like
        </h3>
        <p>{task.completion}</p>
      </section>
      {(inputs.length > 0 || task.waitingFor) && (
        <section className="detail-section">
          <h3>
            <GitBranch size={15} />
            What this needs
          </h3>
          {inputs.map((input) => (
            <button className="dependency-link" key={input.id} onClick={() => onTask(input.id)}>
              <Avatar person={input.owner} small />
              <span>{input.title}</span>
              <ArrowUpRight size={14} />
            </button>
          ))}
          {task.waitingFor && <p>{task.waitingFor}</p>}
        </section>
      )}
      <section className="detail-section">
        <h3>
          <AudioLines size={15} />
          From the conversation
        </h3>
        <div className="evidence-list">
          {!task.evidence.length && (
            <p className="form-note">
              Added directly to the workspace; no meeting quote is attached.
            </p>
          )}
          {task.evidence.map((e, i) => (
            <button
              className="evidence-card"
              key={`${e.meetingId}-${e.segment}-${i}`}
              onClick={() => onEvidence(e)}
            >
              <blockquote>“{e.quote}”</blockquote>
              <span>
                {e.speaker} · {timeLabel(e.seconds)} <ArrowUpRight size={13} />
              </span>
            </button>
          ))}
        </div>
      </section>
      {task.correctedAt && (
        <div className="correction-note">
          <LockKeyhole size={14} />
          <p>
            Your corrected fields are preserved. Other fields can still update from meetings.
            <button onClick={onRelease} disabled={pending}>
              Allow automatic updates again
            </button>
          </p>
        </div>
      )}
    </>
  );
}

export function MeetingDetail({
  id,
  timezone,
  segment,
  revision,
  pending,
  onRetry,
}: {
  id: string;
  segment?: number;
  timezone?: string;
  revision?: string;
  pending: boolean;
  onRetry: () => void;
}) {
  const query = useQuery({
    queryKey: ["meeting", id, revision],
    queryFn: () =>
      api<Meeting>(`/meetings/${id}${revision ? `?revision=${encodeURIComponent(revision)}` : ""}`),
    refetchInterval: (q) =>
      ["processing", "queued"].includes(q.state.data?.state ?? "") ? 4000 : false,
  });
  const [search, setSearch] = useState("");
  useEffect(() => {
    setSearch("");
  }, [id]);
  useEffect(() => {
    if (query.data && segment !== undefined) {
      requestAnimationFrame(() =>
        document.getElementById(`passage-${segment}`)?.scrollIntoView({ block: "center" }),
      );
    }
  }, [query.data?.id, segment]);
  if (query.error)
    return <Empty title="Couldn’t load this conversation" text={query.error.message} />;
  if (!query.data)
    return (
      <div className="loading-panel">
        <Loader2 className="spin" />
        Opening the original conversation…
      </div>
    );
  const meeting = query.data;
  const rows = meeting.segments.filter(
    (s) => !search || `${s.speaker} ${s.text}`.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <>
      <div className="detail-title">
        <span className="task-kicker">
          {meeting.appName} · {dateLabel(meeting.occurredAt, timezone)}
        </span>
        <h2>{meeting.title}</h2>
      </div>
      <div className="meeting-detail-meta">
        <AudioLines size={16} />
        {meeting.segments.length} passages <span>·</span>
        {meeting.state === "archived" ? "Historical source context" : meeting.state}
      </div>
      {meeting.summary && (
        <section className="detail-section">
          <h3>At a glance</h3>
          <p>{meeting.summary}</p>
        </section>
      )}
      {meeting.state === "archived" && (
        <div className="context-note">
          {revision
            ? "This is the exact transcript revision cited by the task."
            : "Retained as historical context. Its old action items have not been added to your current plan."}
        </div>
      )}
      {["queued", "processing"].includes(meeting.state) && (
        <div className="processing-strip">
          <Loader2 size={14} className="spin" />
          {meeting.processedSegments} of {meeting.segments.length} passages read
        </div>
      )}
      {meeting.error && <div className="inline-alert">{meeting.error}</div>}
      <div className="transcript-heading">
        <h3>Original transcript</h3>
        <button
          className="text-button"
          disabled={pending || ["processing", "queued"].includes(meeting.state)}
          onClick={onRetry}
        >
          {meeting.state === "error" ? "Retry analysis" : "Reconcile with current work"}{" "}
          <Sparkles size={13} />
        </button>
      </div>
      <div className="search-box">
        <Search size={16} />
        <input
          aria-label="Search transcript"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Find a word or phrase in this conversation…"
        />
      </div>
      <div className="transcript">
        {rows.map((row) => (
          <div
            className={`transcript-passage ${row.id === segment ? "highlighted" : ""}`}
            id={`passage-${row.id}`}
            key={row.id}
          >
            <div>
              <strong>{row.speaker}</strong>
              <span>{timeLabel(row.start)}</span>
            </div>
            <p>{row.text}</p>
          </div>
        ))}
        {!rows.length && (
          <p className="empty-small">
            {meeting.segments.length
              ? "No matching passages."
              : "This recording contains no transcribed speech."}
          </p>
        )}
      </div>
    </>
  );
}

export function UpdateModal({
  open,
  onClose,
  viewer,
  localMode,
  aiAvailable,
  onSave,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  viewer: Person;
  localMode: boolean;
  aiAvailable: boolean;
  onSave: (value: { text: string; person: Person; requestId?: string }) => Promise<boolean>;
  pending: boolean;
}) {
  const [text, setText] = useState("");
  const [person, setPerson] = useState(viewer);
  const request = useRef({ fingerprint: "", id: "" });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="What’s changed?"
      description="Write it as you’d say it. Workroom will connect the update to your shared plan."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const fingerprint = JSON.stringify({ text, person });
          if (request.current.fingerprint !== fingerprint)
            request.current = { fingerprint, id: crypto.randomUUID() };
          void onSave({ text, person, requestId: request.current.id }).then((ok) => {
            if (ok) {
              setText("");
              request.current = { fingerprint: "", id: "" };
            }
          });
        }}
      >
        <textarea
          aria-label="Work update"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="The store brief is ready. David is taking the next Sound conversation. Let’s park the catalog expansion for now…"
          required
          minLength={2}
          maxLength={10000}
          rows={6}
        />
        <div className="form-footer">
          {localMode ? (
            <label className="inline-select">
              From{" "}
              <select
                aria-label="Update author"
                value={person}
                onChange={(e) => setPerson(e.target.value as Person)}
              >
                <option value="charlton">Charlton</option>
                <option value="david">David</option>
              </select>
            </label>
          ) : (
            <span>{PEOPLE[viewer].name}</span>
          )}
          <button className="button primary" disabled={pending || text.trim().length < 2}>
            {pending ? <Loader2 className="spin" size={15} /> : <ArrowUpRight size={15} />}Share
            update
          </button>
        </div>
        {!aiAvailable && (
          <p className="form-note">
            Your update will be saved and queued until the local processor is available.
          </p>
        )}
      </form>
    </Modal>
  );
}
export function ImportModal({
  open,
  onClose,
  pending,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  pending: boolean;
  onSave: (value: {
    title: string;
    transcript: string;
    occurredAt: string;
    appName: string;
  }) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 16));
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bring in a conversation"
      description="Keep the original words and speaker names. The actual meeting date helps preserve the order of decisions."
      wide
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void onSave({
            title: title || "Imported conversation",
            transcript: text,
            occurredAt: new Date(`${date}Z`).toISOString(),
            appName: "UltraMeet",
          });
        }}
      >
        <label className="form-label">
          Conversation title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Founder sync"
          />
        </label>
        <label className="form-label">
          Meeting time (UTC)
          <input
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </label>
        <label className="form-label">
          Transcript
          <textarea
            rows={9}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Charlton: …&#10;David: …"
            required
          />
        </label>
        <div className="form-footer">
          <span className="form-note">UltraMeet can also send these automatically.</span>
          <button className="button primary" disabled={pending || !text.trim()}>
            <Upload size={15} />
            Import conversation
          </button>
        </div>
      </form>
    </Modal>
  );
}
export function Settings({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const q = useQuery({
    queryKey: ["settings"],
    queryFn: () =>
      api<{
        endpoint: string;
        model: string | null;
        inference: NonNullable<Workspace["inference"]>;
        aiAvailable: boolean;
        localMode: boolean;
        ingestToken: string | null;
      }>("/settings"),
    enabled: open,
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Your workspace"
      description="Keep UltraMeet doing what it does best. Workroom receives the conversation and maintains the shared plan."
    >
      {q.data ? (
        <>
          <div className="settings-card">
            <AudioLines size={21} />
            <div>
              <h3>UltraMeet</h3>
              <p>
                Use UltraMeet’s existing Funday endpoint setting to send completed calls here. Your
                notetaker stays the same.
              </p>
            </div>
          </div>
          <label className="form-label">
            Transcript endpoint
            <div className="copy-field">
              <input readOnly value={q.data.endpoint} />
              <button
                className="icon-button"
                aria-label="Copy transcript endpoint"
                onClick={() => {
                  void navigator.clipboard.writeText(q.data.endpoint).then(() => setCopied(true));
                }}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </label>
          {q.data.ingestToken && (
            <label className="form-label">
              Connection token
              <div className="copy-field">
                <input
                  aria-label="UltraMeet connection token"
                  type={revealed ? "text" : "password"}
                  value={q.data.ingestToken}
                  readOnly
                />
                <button className="text-button" onClick={() => setRevealed(!revealed)}>
                  {revealed ? "Hide" : "Reveal"}
                </button>
                <button
                  className="icon-button"
                  aria-label="Copy connection token"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(q.data.ingestToken!)
                      .then(() => setCopied(true));
                  }}
                >
                  <Copy size={16} />
                </button>
              </div>
            </label>
          )}
          <div className="settings-card">
            <LockKeyhole size={21} />
            <div>
              <h3>Fully private meeting intelligence</h3>
              <p>
                Conversations are analyzed only on our own Mac. Meetings, tasks, and notes are
                never sent to an outside AI company. If the Mac is offline, updates wait safely in
                the queue.
              </p>
            </div>
          </div>
          <div className="setting-row">
            <span>Meeting analysis</span>
            <strong>
              {q.data.inference.status === "ready"
                ? `Running on ${q.data.inference.processingLocation}`
                : q.data.inference.status === "waiting"
                  ? `Queued for ${q.data.inference.processingLocation}`
                  : "Paused · updates are saved"}
            </strong>
          </div>
          <div className="setting-row">
            <span>Cloud AI</span>
            <strong>Never used</strong>
          </div>
          {q.data.model && (
            <div className="setting-row">
              <span>Private model</span>
              <strong>{q.data.model}</strong>
            </div>
          )}
          <div className="setting-row">
            <span>Transcription</span>
            <strong>On-device in UltraMeet</strong>
          </div>
          <div className="setting-row">
            <span>Access</span>
            <strong>
              {q.data.localMode ? "This computer · local mode" : "Private team accounts"}
            </strong>
          </div>
          <p className="form-note">
            Direct task corrections stay protected until you allow automatic updates again.
          </p>
          {!q.data.localMode && <AccountPanel />}
        </>
      ) : (
        <p>{q.error?.message ?? "Loading connection details…"}</p>
      )}
    </Modal>
  );
}
export function Login({ onSuccess }: { onSuccess: (password: string) => void }) {
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const username = String(form.get("username")).trim();
      const password = String(form.get("password"));
      await api("/auth/sign-in/username", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      onSuccess(password);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Sign-in failed");
    } finally {
      setPending(false);
    }
  }
  return (
    <EntryFrame>
      <div className="entry-card sign-in-card">
        <div className="eyebrow">YOUR SHARED WORKSPACE</div>
        <h2>
          Good to have you here<span>.</span>
        </h2>
        <p>
          Sign in to find your next move. First time here? Your temporary login will guide you
          through setup.
        </p>
        <form
          onSubmit={(event) => {
            void submit(event);
          }}
        >
          <label className="form-label">
            Username
            <input
              name="username"
              required
              autoComplete="username"
              placeholder="Your username"
              autoCapitalize="none"
              spellCheck={false}
            />
          </label>
          <label className="form-label">
            Password
            <input name="password" type="password" required autoComplete="current-password" />
          </label>
          {error && <p className="inline-alert">{error}</p>}
          <button className="button primary entry-submit" disabled={pending}>
            {pending ? <Loader2 size={16} className="spin" /> : <ArrowRight size={16} />}Sign in
          </button>
        </form>
        <div className="entry-small">An invitation-only space. Your work stays between you.</div>
      </div>
    </EntryFrame>
  );
}
